
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../../pages/buynowaddress/buynowaddress.css";
import { baseURL } from "../../constants/url";

const PLATFORM_FEE = 8;
const DELIVERY_CHARGE = 49;

const getErrorMessage = (data, fallback) => {
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.error === "string") return data.error;
  return fallback;
};

const readResponse = async (response) => {
  const text = await response.text();

  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
};

const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

const OrderSummaryDetails = ({ selectedAddressId }) => {
  const [addressDetails, setAddressDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [removed, setRemoved] = useState(false);

  const paymentActiveRef = useRef(false);
  const cancellationInProgressRef = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();

  const product = location.state?.product;
  const variant = product?.variants?.[0];

  const productPrice = Number(variant?.final_price || 0);
  const finalAmount =
    productPrice + PLATFORM_FEE + DELIVERY_CHARGE;

  console.log("Final Amount:", finalAmount);

  // ==========================================
  // 1. LOAD RAZORPAY SCRIPT
  // ==========================================

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const existingScript = document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
      );

      if (existingScript) {
        existingScript.addEventListener(
          "load",
          () => resolve(true),
          { once: true }
        );

        existingScript.addEventListener(
          "error",
          () => resolve(false),
          { once: true }
        );

        return;
      }

      const script = document.createElement("script");

      script.src =
        "https://checkout.razorpay.com/v1/checkout.js";

      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);

      document.body.appendChild(script);
    });
  };

  // ==========================================
  // 2. CANCEL ORDER API
  // ==========================================

  const cancelOrder = async (orderId, token) => {
    if (orderId === undefined || orderId === null) {
      console.error("Cancel Order: Order ID missing");
      return false;
    }

    if (cancellationInProgressRef.current) {
      return false;
    }

    cancellationInProgressRef.current = true;

    try {
      console.log("Cancelling Order ID:", orderId);

      const response = await fetch(
        `${baseURL}common/cancel_order/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            order_id: orderId,
          }),
        }
      );

      const data = await readResponse(response);

      console.log("Cancel Order HTTP Status:", response.status);
      console.log("Cancel Order Response:", data);

      if (!response.ok) {
        throw new Error(
          getErrorMessage(data, "Order cancellation failed")
        );
      }

      return true;
    } catch (error) {
      console.error("Cancel Order Error:", error);

      Swal.fire({
        icon: "error",
        title: "Cancellation Failed",
        text:
          error.message ||
          "Unable to cancel order. Please check your order history.",
      });

      return false;
    } finally {
      cancellationInProgressRef.current = false;
    }
  };

  // ==========================================
  // 3. VERIFY PAYMENT API
  // ==========================================

  const verifyPayment = async (response, token) => {
    try {
      setIsLoading(true);

      console.log("Razorpay Payment Response:", response);

      const verifyPayload = {
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_order_id: response.razorpay_order_id,
        razorpay_signature: response.razorpay_signature,
      };

      console.log("Verify Payload:", verifyPayload);

      const verifyRes = await fetch(
        `${baseURL}common/verify_payment/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(verifyPayload),
        }
      );

      const verifyData = await readResponse(verifyRes);

      console.log("Verify HTTP Status:", verifyRes.status);
      console.log("Verify Backend Response:", verifyData);

      if (!verifyRes.ok) {
        throw new Error(
          getErrorMessage(
            verifyData,
            "Payment verification failed"
          )
        );
      }

      await Swal.fire({
        icon: "success",
        title: "Payment Successful!",
        text: "Your payment has been verified successfully.",
        confirmButtonText: "OK",
      });

      navigate("/ordersuccess", {
        state: {
          orderId: verifyData?.order_id,
          deliveryStatus: verifyData?.delivery_status,
          paymentStatus: verifyData?.status,
        },
      });
    } catch (error) {
      console.error("Verify Payment Error:", error);

      await Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text:
          error.message ||
          "Payment verification failed. Please check your payment status.",
      });

      // Do not cancel here:
      // payment may have succeeded even if verification failed.
    } finally {
      paymentActiveRef.current = false;
      setIsLoading(false);
    }
  };

  // ==========================================
  // 4. PLACE ORDER + OPEN RAZORPAY
  // ==========================================

  const loadRazorpay = async () => {
    if (paymentActiveRef.current) return;

    if (!selectedAddressId) {
      Swal.fire(
        "Select Address",
        "Please select delivery address",
        "error"
      );
      return;
    }

    if (!variant?.id) {
      Swal.fire(
        "Error",
        "Product variant not found",
        "error"
      );
      return;
    }

    const token = getAuthToken();

    if (!token) {
      Swal.fire(
        "Authentication Required",
        "Please login again",
        "error"
      );
      return;
    }

    paymentActiveRef.current = true;
    setIsLoading(true);

    let orderCreated = false;

    try {
      const isLoaded = await loadRazorpayScript();

      if (!isLoaded) {
        throw new Error("Razorpay SDK failed to load");
      }

      // CREATE ORDER API

      const res = await fetch(
        `${baseURL}common/create_order/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            products: [
              {
                variant_id: variant.id,
                quantity: 1,
              },
            ],
            address_id: selectedAddressId,
            platform_fee: PLATFORM_FEE,
            delivery_charge: DELIVERY_CHARGE,
          }),
        }
      );

      const data = await readResponse(res);

      console.log("Create Order HTTP Status:", res.status);
      console.log("Create Order Backend Response:", data);

      if (!res.ok) {
        throw new Error(
          getErrorMessage(data, "Order creation failed")
        );
      }

      orderCreated = true;

      if (!data.razorpay_key || !data.order_id) {
        throw new Error(
          "Razorpay details missing in create_order response"
        );
      }

      // IMPORTANT:
      // The cancel API needs your database order ID.
      // Do not use the Razorpay order ID (order_...)
      // as the database ID.
      //
      // Change this mapping if your backend uses
      // a different property name.

      const databaseOrderId =
        data?.db_order_id ??
        data?.order_db_id ??
        data?.order?.id ??
        (typeof data?.order_id === "number"
          ? data.order_id
          : null);

      const razorpayOrderId =
        data?.razorpay_order_id ??
        data?.order_id;

      let paymentSucceeded = false;
      let paymentVerificationStarted = false;
      let popupDismissed = false;

      // ======================================
      // 5. RAZORPAY OPTIONS
      // ======================================

      const options = {
        key: data.razorpay_key,

        // Assumes data.amount is in rupees.
        // If backend sends paise, use data.amount directly.
        amount: Math.round(Number(data.amount) * 100),

        currency: data.currency || "INR",

        name: product.name,
        description: "Order Payment",

        order_id: razorpayOrderId,

        // PAYMENT SUCCESS

        handler: async function (response) {
          paymentSucceeded = true;
          paymentVerificationStarted = true;

          console.log("Payment Success:", response);

          await verifyPayment(response, token);
        },

        // PAYMENT POPUP CLOSE

        modal: {
          escape: true,
          backdropclose: false,

          ondismiss: async function () {
            console.log("Razorpay Popup Closed");

            if (
              popupDismissed ||
              paymentSucceeded ||
              paymentVerificationStarted
            ) {
              return;
            }

            popupDismissed = true;

            // A failed payment attempt can still be
            // processing. Backend must verify payment
            // status before allowing cancellation.

            try {
              if (databaseOrderId === null) {
                await Swal.fire({
                  icon: "warning",
                  title: "Payment Not Completed",
                  text:
                    "Order cancellation requires the database order ID from create_order API.",
                });
                return;
              }

              const cancelled = await cancelOrder(
                databaseOrderId,
                token
              );

              if (cancelled) {
                await Swal.fire({
                  icon: "info",
                  title: "Order Cancelled",
                  text:
                    "Payment was not completed. Your order has been cancelled.",
                  confirmButtonText: "OK",
                });
              }
            } finally {
              paymentActiveRef.current = false;
              setIsLoading(false);
            }
          },
        },

        // PAYMENT FAILED

        // A failure event does not necessarily mean
        // the complete checkout is closed.
        // Cancellation is handled on modal dismiss.

        retry: {
          enabled: true,
        },

        theme: {
          color: "#c18b13",
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", function (response) {
        console.error(
          "Razorpay Payment Failed:",
          response.error
        );
      });

      setIsLoading(false);
      razorpay.open();
    } catch (error) {
      console.error("Place Order Error:", error);

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: orderCreated
          ? `${error.message}. An order may have been created; please check your order history.`
          : error.message,
      });

      paymentActiveRef.current = false;
      setIsLoading(false);
    }
  };

  // ==========================================
  // 6. FETCH ADDRESS DETAILS
  // ==========================================

  useEffect(() => {
    let ignore = false;

    const fetchAddress = async () => {
      if (!selectedAddressId) {
        setAddressDetails(null);
        return;
      }

      try {
        const token = getAuthToken();

        const res = await fetch(
          `${baseURL}common/addresses/${selectedAddressId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await readResponse(res);

        if (!res.ok) {
          throw new Error(
            getErrorMessage(data, "Failed to fetch address")
          );
        }

        if (!ignore) {
          setAddressDetails(data);
        }
      } catch (error) {
        console.error("Fetch Address Error:", error);
      }
    };

    fetchAddress();

    return () => {
      ignore = true;
    };
  }, [selectedAddressId]);

  // ==========================================
  // 7. REMOVE PRODUCT
  // ==========================================

  const handleRemove = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this product?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
    });

    if (!result.isConfirmed) return;

    setIsLoading(true);

    try {
      const token = getAuthToken();

      const response = await fetch(
        `${baseURL}common/cart_delete/${product.id}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to remove product");
      }

      setRemoved(true);

      await Swal.fire({
        icon: "success",
        title: "Removed!",
        text: "Product removed from cart.",
      });

      navigate(-1);
    } catch (error) {
      console.error("Remove Product Error:", error);

      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================
  // 8. PRODUCT VALIDATION
  // ==========================================

  if (!product || !variant || removed) {
    return <p>No product selected.</p>;
  }

  const discountPercentage =
    variant.price && variant.final_price
      ? Math.round(
          ((variant.price - variant.final_price) /
            variant.price) *
            100
        )
      : 0;

  // ==========================================
  // 9. JSX DESIGN
  // ==========================================

  return (
    <div className="card_common mt_20">
      <h6>Order Summary</h6>

      <div className="product_detail_block pt_20 pb_20 border_bottom">
        <div className="grid_three_one">
          <div className="prduct_imgs">
            <img
              src={variant?.images?.[0]?.image}
              alt={product.name}
            />
          </div>

          <div className="prduct_detailss">
            <h6>{product.name}</h6>

            {product?.size?.length > 0 && (
              <p>Size: {product.size[0]}</p>
            )}

            {product?.colors?.length > 0 && (
              <p>Color: {product.colors[0]}</p>
            )}

            {product?.weight?.length > 0 && (
              <p>Weight: {product.weight[0]}</p>
            )}

            <div className="d_flex gap_10 pt_10 align_center">
              <p>₹{variant.final_price}</p>

              {variant.price && (
                <>
                  <p className="font_bold">
                    <s>₹{variant.price}</s>
                  </p>

                  <p className="color_green">
                    {discountPercentage}% Off
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt_15">
          <button
            type="button"
            className="remove_btn_common font_bold"
            onClick={handleRemove}
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : "Remove"}
          </button>
        </div>
      </div>

      <div className="order_btn">
        <div className="d_flex justify_sp_end">
          <button
            className="saveForm"
            type="button"
            onClick={loadRazorpay}
            disabled={
              isLoading ||
              !selectedAddressId ||
              removed
            }
          >
            {isLoading ? "Processing..." : "Place Order"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderSummaryDetails;
